import { useEffect, useState } from "react";
import api from "@/lib/api";
import { dt } from "@/lib/format";
import { PageHeader, Loader } from "@/components/Common";
import { Badge } from "@/components/ui/badge";
import {
  Activity, Database, ListChecks, AlertTriangle, MessageCircle, TrendingUp, CheckCircle2, XCircle,
} from "lucide-react";

function Stat({ label, value, tone = "default" }) {
  const toneClass = tone === "danger" ? "text-red-500" : tone === "ok" ? "text-emerald-600" : "text-foreground";
  return (
    <div className="rounded-2xl border border-border px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-xl font-head font-semibold mt-1 ${toneClass}`}>{value}</p>
    </div>
  );
}

export default function SystemStatus() {
  const [data, setData] = useState(null);

  const load = () => api.get("/estado-sistema").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  if (!data) return <div className="p-4 sm:p-8"><Loader /></div>;

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto">
      <PageHeader title="Estado del sistema" subtitle="Salud de la base, colas de trabajo y uso del agente." />

      <div className="ms-card p-5 mb-4 flex items-center gap-3" data-testid="db-health-card">
        {data.base_datos.ok ? (
          <><CheckCircle2 className="h-6 w-6 text-emerald-500" /><div><p className="font-semibold text-sm">Base de datos conectada</p><p className="text-xs text-muted-foreground">Todo funcionando con normalidad.</p></div></>
        ) : (
          <><XCircle className="h-6 w-6 text-red-500" /><div><p className="font-semibold text-sm">Problema con la base de datos</p><p className="text-xs text-muted-foreground">No se pudo verificar la conexión.</p></div></>
        )}
      </div>

      <h2 className="font-head text-lg font-semibold mb-3 flex items-center gap-2"><ListChecks className="h-5 w-5 text-primary" /> Colas de trabajo</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="colas-grid">
        <Stat label="Seguimientos pendientes" value={data.colas.seguimientos_pendientes} />
        <Stat label="Seguimientos fallidos" value={data.colas.seguimientos_fallidos} tone={data.colas.seguimientos_fallidos > 0 ? "danger" : "default"} />
        <Stat label="Cola Shopify" value={data.colas.shopify_cola} />
        <Stat label="Errores Shopify" value={data.colas.shopify_errores} tone={data.colas.shopify_errores > 0 ? "danger" : "default"} />
        <Stat label="Facturas pendientes" value={data.colas.fiscal_pendientes} />
        <Stat label="Errores de facturación" value={data.colas.fiscal_errores} tone={data.colas.fiscal_errores > 0 ? "danger" : "default"} />
        <Stat label="Webhooks fallidos" value={data.colas.webhooks_fallidos} tone={data.colas.webhooks_fallidos > 0 ? "danger" : "default"} />
      </div>

      <h2 className="font-head text-lg font-semibold mb-3 flex items-center gap-2"><MessageCircle className="h-5 w-5 text-primary" /> Uso del agente</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6" data-testid="uso-agente-grid">
        <Stat label="Conversaciones hoy" value={data.uso_agente.conversaciones_hoy} />
        <Stat label="Conversaciones (semana)" value={data.uso_agente.conversaciones_semana} />
        <Stat label="% resueltas sin humano hoy" value={`${data.uso_agente.pct_resueltas_sin_humano_hoy}%`} tone="ok" />
        <Stat label="Derivaciones hoy" value={data.uso_agente.derivaciones_hoy} />
        <Stat label="Derivaciones (semana)" value={data.uso_agente.derivaciones_semana} />
        <Stat label="Pedidos del agente hoy" value={data.uso_agente.pedidos_creados_por_agente_hoy} />
      </div>

      <h2 className="font-head text-lg font-semibold mb-3 flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-primary" /> Últimos errores</h2>
      {data.ultimos_errores.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center ms-card">Sin errores recientes. ✨</p>
      ) : (
        <div className="space-y-2" data-testid="ultimos-errores-list">
          {data.ultimos_errores.map((e) => (
            <div key={e.id} className="ms-card p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <Badge className="rounded-full border-0 bg-red-50 text-red-600 capitalize mr-2">{e.origen}</Badge>
                <span className="text-sm truncate">{e.detalle || e.resultado || "Error"}</span>
              </div>
              <span className="text-[11px] text-muted-foreground shrink-0">{dt(e.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
